import React from 'react'
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  Divider,
  Paper,
  Button,
  Rating,
  alpha,
} from '@mui/material'
import {
  Edit,
  Delete,
  LocationOn,
  Person,
  Phone,
  Notes,
  Save,
  Build,
  CarRepair,
  DirectionsCar,
  Storefront,
  AttachMoney,
  Inventory2,
} from '@mui/icons-material'
import { PickupLocation, RentalCompanyLocation, ShopType, VendorLocation, TowCompanyLocation } from '@otw/models'
import { formatRepairShopCapacityLabel, type RepairShopTruckCounts } from '@/hooks/logistics/useLogistics'

interface RepairShopLocation extends PickupLocation {
  shop_type?: ShopType[]
  name?: string
  grade?: string
  communication?: number
  price?: number
  speed?: number
  labor_rate?: number
  capacity?: number
  secondary_phone?: string
  diesel?: boolean
  is_active?: boolean
  inactive_at?: Date | null
  inactive_note?: string | null
}

type AnyLocation = PickupLocation | RepairShopLocation | RentalCompanyLocation | VendorLocation | TowCompanyLocation
type ManagedLocationType = 'pickup' | 'repair_shop' | 'rental_company' | 'vendor' | 'tow_company'

const locationTypeNoun = (locationType: ManagedLocationType) => {
  switch (locationType) {
    case 'pickup':
      return 'pickup'
    case 'rental_company':
      return 'rental company'
    case 'vendor':
      return 'vendor'
    case 'tow_company':
      return 'tow company'
    default:
      return 'repair shop'
  }
}

interface LocationCardListProps {
  locations: AnyLocation[]
  locationType: ManagedLocationType
  onEdit: (location: AnyLocation) => void
  onDelete?: (id: string) => void
  onRatingUpdate?: (locationId: string, field: 'communication' | 'price' | 'speed', value: number | null) => void
  repairShopTruckCountsById?: Record<string, RepairShopTruckCounts>
  capacityNumberFormatter?: Intl.NumberFormat
}

const getShopTypeLabel = (shopType: ShopType): string => {
  switch (shopType) {
    case 'mechanic_shop':
      return 'Mechanic'
    case 'body_shop':
      return 'Body Shop'
    case 'parts':
      return 'Parts'
    case 'mobile':
      return 'Mobile'
    case 'other':
      return 'Other'
    default:
      return 'Unknown'
  }
}

const getShopTypeIcon = (shopType: ShopType) => {
  switch (shopType) {
    case 'mechanic_shop':
      return <Build sx={{ fontSize: 14 }} />
    case 'body_shop':
      return <CarRepair sx={{ fontSize: 14 }} />
    case 'mobile':
      return <DirectionsCar sx={{ fontSize: 14 }} />
    default:
      return <LocationOn sx={{ fontSize: 14 }} />
  }
}

const getShopTypeColor = (
  shopType: ShopType,
): 'primary' | 'secondary' | 'info' | 'success' | 'default' => {
  switch (shopType) {
    case 'mechanic_shop':
      return 'primary'
    case 'body_shop':
      return 'secondary'
    case 'parts':
      return 'info'
    case 'mobile':
      return 'success'
    default:
      return 'default'
  }
}

const formatChargeRange = (min?: number | null, max?: number | null): string | null => {
  if (min == null && max == null) return null
  if (min != null && max != null) {
    return `$${min.toLocaleString()} – $${max.toLocaleString()}/wk`
  }
  if (min != null) return `$${min.toLocaleString()}+/wk`
  return `Up to $${max!.toLocaleString()}/wk`
}

const LocationCardList: React.FC<LocationCardListProps> = ({
  locations,
  locationType,
  onEdit,
  onDelete,
  onRatingUpdate,
  repairShopTruckCountsById,
  capacityNumberFormatter,
}) => {
  if (!locations || locations.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <LocationOn sx={{ fontSize: 56, color: 'text.secondary', mb: 1.5 }} />
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 0.5 }}>
          No {locationTypeNoun(locationType)} locations found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add your first {locationTypeNoun(locationType)} location to get started
        </Typography>
      </Paper>
    )
  }

  return (
    <Stack spacing={1.5}>
      {locations
        .filter((location) => location && location.id)
        .map((location) => {
          const isPreviousAddress = location.id?.startsWith('previous-')
          const repairLoc = location as RepairShopLocation
          const rentalLoc = location as RentalCompanyLocation
          const vendorLoc = location as VendorLocation
          const towLoc = location as TowCompanyLocation
          const pickupLoc = location as PickupLocation

          // Title and subtitle
          let title = ''
          let subtitle = ''
          if (locationType === 'repair_shop') {
            title = repairLoc.name || repairLoc.address || 'Repair Shop'
          } else if (locationType === 'rental_company') {
            title = rentalLoc.business_name || rentalLoc.address || 'Rental Company'
          } else if (locationType === 'vendor') {
            title = vendorLoc.name || vendorLoc.address || 'Vendor'
          } else if (locationType === 'tow_company') {
            title = towLoc.name || towLoc.address || 'Tow Company'
          } else {
            title = pickupLoc.company_name || pickupLoc.address || 'Pickup Location'
          }
          const addressLine = location.address
          const cityStateZip = [
            location.city,
            [location.state, location.zip].filter(Boolean).join(' '),
          ]
            .filter(Boolean)
            .join(', ')
          subtitle = [addressLine, cityStateZip].filter(Boolean).join(' • ')

          const chargePerWeek = formatChargeRange(
            rentalLoc.charge_per_week_min,
            rentalLoc.charge_per_week_max,
          )

          return (
            <Card
              key={location.id}
              variant="outlined"
              sx={{
                borderRadius: 2,
                borderColor:
                  locationType === 'repair_shop' && repairLoc.is_active === false
                    ? 'warning.main'
                    : 'divider',
                bgcolor:
                  locationType === 'repair_shop' && repairLoc.is_active === false
                    ? (theme) => alpha(theme.palette.warning.main, 0.08)
                    : undefined,
                transition: 'border-color 120ms ease, box-shadow 120ms ease',
                '&:hover': { borderColor: 'primary.main' },
              }}
            >
              <CardContent sx={{ pb: 1.5 }}>
                {/* Header: title + key chips */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1.25 }}>
                        {title}
                      </Typography>
                      {locationType === 'repair_shop' && (
                        <Chip
                          label={repairLoc.is_active === false ? 'Inactive' : 'Active'}
                          size="small"
                          color={repairLoc.is_active === false ? 'warning' : 'success'}
                          variant={repairLoc.is_active === false ? 'filled' : 'outlined'}
                          sx={{ height: 22, fontWeight: 700, fontSize: '0.75rem' }}
                          title={repairLoc.inactive_note || undefined}
                        />
                      )}
                    </Box>
                    {locationType === 'repair_shop' && repairLoc.grade && (
                      <Typography variant="caption" color="text.secondary">
                        Grade {repairLoc.grade}
                      </Typography>
                    )}
                    {locationType === 'tow_company' && towLoc.grade && (
                      <Typography variant="caption" color="text.secondary">
                        Grade {towLoc.grade}
                      </Typography>
                    )}
                    {locationType === 'repair_shop' &&
                      repairLoc.is_active === false &&
                      repairLoc.inactive_note && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          {repairLoc.inactive_note}
                        </Typography>
                      )}
                  </Box>
                  {locationType === 'repair_shop' && repairLoc.shop_type && repairLoc.shop_type.length > 0 && (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      flexWrap="wrap"
                      sx={{ flexShrink: 0, justifyContent: 'flex-end', rowGap: 0.5 }}
                    >
                      {repairLoc.shop_type.slice(0, 2).map((type) => (
                        <Chip
                          key={type}
                          icon={getShopTypeIcon(type)}
                          label={getShopTypeLabel(type)}
                          color={getShopTypeColor(type)}
                          size="small"
                          sx={{ height: 22, fontSize: '0.7rem' }}
                        />
                      ))}
                      {repairLoc.shop_type.length > 2 && (
                        <Chip
                          label={`+${repairLoc.shop_type.length - 2}`}
                          size="small"
                          sx={{ height: 22, fontSize: '0.7rem' }}
                        />
                      )}
                    </Stack>
                  )}
                </Box>

                {/* Address */}
                {subtitle && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 1 }}>
                    <LocationOn sx={{ fontSize: 16, color: 'text.secondary', mt: '2px' }} />
                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                      {subtitle}
                    </Typography>
                  </Box>
                )}

                {/* Repair shop ratings */}
                {locationType === 'repair_shop' && (
                  <>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr',
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      {(['communication', 'speed', 'price'] as const).map((field) => {
                        const value = (repairLoc[field] ?? 0) as number
                        const label = field === 'communication' ? 'Comm' : field === 'speed' ? 'Speed' : 'Price'
                        return (
                          <Box key={field} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <Typography variant="caption" color="text.secondary">
                              {label}
                            </Typography>
                            <Rating
                              value={value}
                              max={5}
                              size="small"
                              precision={0.5}
                              readOnly={!onRatingUpdate || isPreviousAddress}
                              onChange={(_e, newValue) => {
                                if (location.id && onRatingUpdate) {
                                  onRatingUpdate(location.id, field, newValue)
                                }
                              }}
                              sx={{ '& .MuiRating-iconFilled': { color: 'primary.main' } }}
                            />
                          </Box>
                        )
                      })}
                    </Box>
                    {(repairLoc.labor_rate != null || repairLoc.capacity != null) && (
                      <Stack direction="row" spacing={2} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                        {repairLoc.labor_rate != null && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AttachMoney sx={{ fontSize: 16, color: 'primary.main' }} />
                            <Typography variant="body2" color="primary.main" fontWeight={500}>
                              ${Number(repairLoc.labor_rate).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}/hr
                            </Typography>
                          </Box>
                        )}
                        {repairLoc.capacity != null && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Inventory2 sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              Capacity:{' '}
                              {repairShopTruckCountsById && capacityNumberFormatter
                                ? formatRepairShopCapacityLabel(
                                    repairLoc,
                                    repairLoc.id
                                      ? repairShopTruckCountsById[repairLoc.id]
                                      : undefined,
                                    capacityNumberFormatter
                                  )
                                : repairLoc.capacity}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    )}
                  </>
                )}

                {locationType === 'tow_company' && (
                  <>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr',
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      {(['communication', 'speed', 'price'] as const).map((field) => {
                        const value = (towLoc[field] ?? 0) as number
                        const label = field === 'communication' ? 'Comm' : field === 'speed' ? 'Speed' : 'Price'
                        return (
                          <Box key={field} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <Typography variant="caption" color="text.secondary">
                              {label}
                            </Typography>
                            <Rating
                              value={value}
                              max={5}
                              size="small"
                              precision={0.5}
                              readOnly={!onRatingUpdate || isPreviousAddress}
                              onChange={(_e, newValue) => {
                                if (location.id && onRatingUpdate) {
                                  onRatingUpdate(location.id, field, newValue)
                                }
                              }}
                              sx={{ '& .MuiRating-iconFilled': { color: 'primary.main' } }}
                            />
                          </Box>
                        )
                      })}
                    </Box>
                    {towLoc.towing_radius_miles != null && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Towing radius: {towLoc.towing_radius_miles.toLocaleString('en-US')} mi
                      </Typography>
                    )}
                  </>
                )}

                {/* Rental company specifics */}
                {locationType === 'rental_company' && (
                  <Stack spacing={0.75} sx={{ mb: 1 }}>
                    {rentalLoc.vehicle_types && rentalLoc.vehicle_types.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {rentalLoc.vehicle_types.slice(0, 5).map((t) => (
                          <Chip
                            key={t}
                            label={t}
                            size="small"
                            variant="outlined"
                            sx={{ height: 22, fontSize: '0.7rem' }}
                          />
                        ))}
                        {rentalLoc.vehicle_types.length > 5 && (
                          <Chip
                            label={`+${rentalLoc.vehicle_types.length - 5}`}
                            size="small"
                            sx={{ height: 22, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    )}
                    {rentalLoc.states_rent_in && rentalLoc.states_rent_in.length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        Rents in: {rentalLoc.states_rent_in.join(', ')}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                      {rentalLoc.fleet_size != null && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Storefront sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            Fleet: {rentalLoc.fleet_size}
                          </Typography>
                        </Box>
                      )}
                      {chargePerWeek && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AttachMoney sx={{ fontSize: 16, color: 'primary.main' }} />
                          <Typography variant="body2" color="primary.main" fontWeight={500}>
                            {chargePerWeek}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Stack>
                )}

                {/* Contact section */}
                {(location.contact_name ||
                  location.contact_phone ||
                  (locationType === 'pickup' &&
                    (pickupLoc.secondary_contact_name || pickupLoc.secondary_contact_phone)) ||
                  (locationType === 'repair_shop' && repairLoc.secondary_phone)) && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Stack spacing={0.5}>
                      {location.contact_name && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">{location.contact_name}</Typography>
                        </Box>
                      )}
                      {location.contact_phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography
                            variant="body2"
                            component="a"
                            href={`tel:${location.contact_phone}`}
                            sx={{ color: 'inherit', textDecoration: 'none' }}
                          >
                            {location.contact_phone}
                          </Typography>
                        </Box>
                      )}
                      {locationType === 'pickup' && pickupLoc.secondary_contact_name && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {pickupLoc.secondary_contact_name} (secondary)
                          </Typography>
                        </Box>
                      )}
                      {locationType === 'pickup' && pickupLoc.secondary_contact_phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography
                            variant="body2"
                            component="a"
                            href={`tel:${pickupLoc.secondary_contact_phone}`}
                            sx={{ color: 'text.secondary', textDecoration: 'none' }}
                          >
                            {pickupLoc.secondary_contact_phone} (secondary)
                          </Typography>
                        </Box>
                      )}
                      {locationType === 'repair_shop' && repairLoc.secondary_phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography
                            variant="body2"
                            component="a"
                            href={`tel:${repairLoc.secondary_phone}`}
                            sx={{ color: 'text.secondary', textDecoration: 'none' }}
                          >
                            {repairLoc.secondary_phone} (secondary)
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </>
                )}

                {/* Notes */}
                {location.notes && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                      <Notes sx={{ fontSize: 16, color: 'text.secondary', mt: '2px' }} />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontStyle: 'italic', wordBreak: 'break-word' }}
                      >
                        {location.notes}
                      </Typography>
                    </Box>
                  </>
                )}
              </CardContent>

              <CardActions
                sx={{
                  justifyContent: 'flex-end',
                  pt: 0,
                  px: 2,
                  pb: 1.5,
                  gap: 0.5,
                }}
              >
                {isPreviousAddress ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Save />}
                    onClick={() => onEdit(location)}
                  >
                    Save
                  </Button>
                ) : (
                  <>
                    <Tooltip title="Edit">
                      <IconButton size="small" color="primary" onClick={() => onEdit(location)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {onDelete && (
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => location.id && onDelete(location.id)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </>
                )}
              </CardActions>
            </Card>
          )
        })}
    </Stack>
  )
}

export default LocationCardList
